/**
 * `/internal/models*` contract types — ops-facing model catalog CRUD
 * over platform-api. See ADR-003 (schema) and ADR-004 (contract).
 *
 * Callers: ops-api (ops-console model registration, pricing updates, protocol
 * assignment). Hot-path readers (gateway-worker) still go through
 * `/internal/pricing/models`, which joins models + current pricing + protocols.
 *
 * Wire conventions:
 *  - All payloads snake_case to match existing `/internal/*` contracts and the
 *    underlying SQL column names. (Task spec used camelCase but the rest of the
 *    codebase is rigorously snake_case on the wire; routing-layer drift is the
 *    higher-cost mistake.)
 *  - `effective_from` is ISO-8601 UTC (e.g. `2026-04-18T00:00:00Z`).
 *  - Numeric pricing fields are non-negative finite USD per million tokens
 *    (matches `input_per_mtok_usd` style already in `internal-billing.ts`).
 */
import type { ArkTaskAdmissionSurface } from './ark-task-admission.ts';
import type { UpstreamProtocol } from './internal-upstreams.ts';
import type { MeteringProfileKind, UnitUsageUnit } from './metering.ts';
import type { MeteringSpec } from './metering-spec.ts';

/**
 * Known protocols that can serve a (vendor, model). Phase 1 shipped the
 * native chat protocols; cross-protocol rows (e.g. openai model served over
 * the anthropic protocol) are deferred to Phase 2 when the pool's protocol
 * factory lands (ADR-003 §model_protocols).
 *
 * `'ark_video_v1'` (#1949 A) is the Ark-official-compatible ASYNCHRONOUS
 * TASK protocol — submit + poll on `/v1/tasks/*`, ADR-013 — and it is a
 * member because the task wire is not any of the three chat wires: it was
 * parked under `'openai'` only while this enum had no honest position for
 * it. This axis names the SALES FORM a registration is sold on (protocol +
 * metering profile); it is NOT `UpstreamProtocol`, the wire the gateway
 * dials — the exhaustive {@link SERVING_UPSTREAM_PROTOCOL} table maps one to
 * the other (an `'ark_video_v1'` registration is served off the
 * OpenAI-compatible upstream ladder).
 *
 * NAMING RULE (owner decision, 2026-08-17): a member is named
 * `<protocol family>_<form>_<version>` — `ark_video_v1`, and a future
 * `grok_video_v1` would follow the same shape. The bare family name is too
 * greedy: Ark is a product line with many surfaces, and this member names
 * exactly its v1 video task face. The three chat members (`anthropic`,
 * `openai`, `gemini`) are historical and stay as they are; every NEW member
 * follows the rule.
 *
 * ADDING A MEMBER: every runtime consumer is an exhaustive `Record` keyed by
 * this union (`MODEL_PROTOCOL_METERING_PROFILES`,
 * `MODEL_PROTOCOL_DEFAULT_PROFILE`, `SERVING_UPSTREAM_PROTOCOL`), so a new
 * member fails compilation at each seat until its row — and therefore its
 * pairing, default and serving ladder — is written down. That is #1949's
 * acceptance criterion: no silent fall-through to an `openai` arm.
 */
export type ModelProtocol =
  | 'anthropic'
  | 'openai'
  | 'gemini'
  | 'ark_video_v1'
  | 'grok_video_v1'
  | 'grok_image_v1';

/** Runtime guard for untrusted input; callees validate before INSERT. */
export const KNOWN_MODEL_PROTOCOLS: readonly ModelProtocol[] = [
  'anthropic',
  'openai',
  'gemini',
  'ark_video_v1',
  'grok_video_v1',
  'grok_image_v1',
] as const;

/**
 * Vendor slugs that mean a native protocol, beyond the protocol's own name.
 *
 * ONE table. It had four hand-written copies in `platform-api` before #1902 and
 * a fifth in `ops-console`; a member added to some of them and not the others
 * does not fail loudly — it silently prices a model on the wrong profile. Every
 * consumer, TypeScript or SQL, derives from here.
 *
 * `openai` is the fall-through and therefore has no list: an unknown vendor
 * slug is treated as OpenAI-compatible, which is what a third-party upstream
 * usually is.
 */
/**
 * An ORDERED list of `[protocol, aliases]` pairs, not a record. Two reasons,
 * and the second is the load-bearing one:
 *
 *  - iterating a record yields `string` keys, which forced a `ModelProtocol`
 *    assertion at the one place that reads them;
 *  - `vendorMeteringProtocolSql` renders one `CASE WHEN` arm per entry, and
 *    `CASE` is first-match-wins. Arm order is therefore semantic. Leaving it to
 *    `Object.entries`' insertion order made a SQL semantic depend on how the
 *    object literal happened to be written; here it is the array's order,
 *    which is what it always meant.
 */
export const MODEL_PROTOCOL_VENDOR_ALIASES: readonly (
  readonly [Exclude<ModelProtocol, 'openai'>, readonly string[]]
)[] = [
  ['anthropic', ['anthropic']],
  ['gemini', ['gemini', 'google', 'google-gemini']],
];

/**
 * The METERING protocol a vendor slug implies when nothing more specific is
 * known — which dimensions a bill for this vendor can carry.
 *
 * Lives here rather than in `platform-api` because the operator console needs
 * the identical answer: it renders the pricing dimensions it is about to
 * submit, and a console that resolved a vendor differently from the write face
 * would show a form for the wrong metering profile (#1902 D).
 *
 * The `'openai'` fallback is the assumption stated above — a third-party slug
 * is usually an OpenAI-compatible upstream — and is deliberately NOT the
 * fallback the gateway's two vendor→protocol functions use (#1929 P1-c). They
 * answer different questions: `generation/protocol-defaults.ts` falls back to
 * `'anthropic'` for "which protocol should the candidate ladder try first"
 * (the #1355 ladder overrides it), and `upstream-capabilities/records.ts`
 * falls back to `null` for "can a probe protocol be classified from this slug
 * at all". `apps/gateway-worker/test/vendor-protocol-fallback-agreement.test.ts`
 * holds all three shut and records why unifying them would break live supply.
 */
export function defaultMeteringProtocolForVendor(vendor: string): ModelProtocol {
  const normalized = vendor.toLowerCase();
  for (const [protocol, aliases] of MODEL_PROTOCOL_VENDOR_ALIASES) {
    if (aliases.includes(normalized)) return protocol;
  }
  return 'openai';
}

/**
 * Which pricing dimensions a row's vendor/profile actually charges.
 *
 * The rule: `cache_write_5m` / `cache_write_1h` exist only on the Anthropic
 * metering profile, and `reasoning` exists only on the Gemini one (where
 * `cache_write` is likewise fixed at 0). Every reader masks the dimensions its
 * row cannot charge, so a tenant never reads a unit price for a dimension its
 * bill will never carry — and never reads `null` for one its bill DOES carry.
 *
 * `meteringProfile` is the authority when the caller has it: an aggregator
 * reselling Gemini under its own vendor slug is priced by the profile, not by
 * the slug. A caller whose query does not join `model_metering_profiles` passes
 * `undefined` and resolves by vendor alone.
 */
export interface PricingModeRow {
  readonly vendor: string;
  readonly meteringProfile?: MeteringProfileKind | null | undefined;
}

export function isGeminiPricingMode(row: PricingModeRow): boolean {
  if (row.meteringProfile !== null && row.meteringProfile !== undefined) {
    return row.meteringProfile === 'gemini_generate_content_v1';
  }
  return defaultMeteringProtocolForVendor(row.vendor) === 'gemini';
}

export function isAnthropicPricingMode(row: PricingModeRow): boolean {
  if (row.meteringProfile !== null && row.meteringProfile !== undefined) {
    return row.meteringProfile === 'anthropic_messages_v2';
  }
  return defaultMeteringProtocolForVendor(row.vendor) === 'anthropic';
}

/**
 * The metering profile a protocol registration takes when the caller omits
 * one, or `null` when the protocol has no default and the caller MUST name
 * one explicitly (the write face 400s an omission).
 *
 * Shared rather than owned by the write face: the operator console renders the
 * same choice it is about to submit, and a console that disagreed with the
 * platform would offer a picker whose options 400.
 *
 * `ark_video_v1: null` is a decision, not a gap (#1949 A). The protocol admits
 * five profiles — `ark_video_token_v1` (per-token, video-input only),
 * `ark_video_token_tiered_v1` (per-token, video-input × resolution),
 * `duration_seconds_v1` (per-second by resolution), `ark_video_seconds_v1`
 * (per-second on the video-input x resolution matrix) and
 * `ark_video_seconds_ref_v1` (per-second by resolution plus a reference-image
 * surcharge) — and they are different SALES FORMS of the same task wire, not
 * a common case and variants: which one a SKU bills on is the commercial
 * decision the registration exists to record.
 * Defaulting any would let an omitted field silently pick a billing model,
 * which is the silent-mispricing direction everything in this file leans
 * against.
 *
 * `grok_video_v1: null` is the same decision for the first applicant of this
 * window (#1949 B / #2138). The protocol currently admits one profile
 * (`grok_video_seconds_v1`), and still has no default: a future second form
 * of the same wire must not inherit a silent pick, and an omitted field
 * 400s with the choices named.
 *
 * `grok_image_v1: null` is the same decision for the still-image form
 * (#2190). The protocol currently admits one profile (`grok_image_unit_v1`)
 * and still has no default.
 *
 * An exhaustive `Record`, not an if-chain: a `ModelProtocol` member added
 * without a row here is a compile error, never a fall-through default.
 */
const MODEL_PROTOCOL_DEFAULT_PROFILE: Readonly<
  Record<ModelProtocol, MeteringProfileKind | null>
> = {
  anthropic: 'anthropic_messages_v2',
  openai: 'openai_responses_v2',
  gemini: 'gemini_generate_content_v1',
  ark_video_v1: null,
  grok_video_v1: null,
  grok_image_v1: null,
};

export function defaultMeteringProfileForProtocol(
  protocol: ModelProtocol,
): MeteringProfileKind | null {
  return MODEL_PROTOCOL_DEFAULT_PROFILE[protocol];
}

/**
 * Which profiles a protocol admits — the protocol↔profile pairing table.
 *
 * An exhaustive `Record`, not an if-chain (#1949 A): the old shape had
 * `openai` as the fall-through arm, so a `ModelProtocol` member added without
 * touching this function silently admitted the whole OpenAI profile set. Now
 * a new member does not compile until its row is written down.
 *
 * Anthropic and Gemini each have exactly one real choice; `openai` is where
 * `openai_images_v1` lives — the profile that authorizes the image lane
 * (#1557). `legacy_4_bucket` is admitted on the three CHAT protocols as the
 * pre-profile fallback; it is deliberately NOT admitted on `ark_video_v1`,
 * which post-dates the profile system and has no pre-profile history to fall
 * back to.
 *
 * The unit-metered kinds (#1650/#1651, per-image #1668) live on `openai`
 * only, and only because a migration widened the profile_kind CHECK first
 * (the 0016 rule: validation that admits a kind the CHECK refuses surfaces as
 * an unmapped 500) — 0020 for seconds/characters/per-call, 0021 for
 * `openai_images_unit_v1`, 0033 for the #1839 rename of the first two. They
 * are OpenAI-surface SKUs, so the anthropic and gemini protocols keep refusing
 * them until a real SKU needs otherwise: the minimal face, widened
 * deliberately, never by default.
 *
 * #1839 renamed `openai_videos_v1` / `tts_characters_v1` to
 * `duration_seconds_v1` / `text_characters_v1` and deliberately left the
 * allow set untouched: the new names describe the billed unit, not a wider
 * protocol face, and widening the face is an independent semantic change that
 * must arrive with the SKU that needs it.
 *
 * `ark_video_v1` (#1949 A) admits exactly the task-ingress profiles
 * (`ARK_TASK_INGRESS_PROFILE_KINDS` in metering.ts, held equal by test):
 * `ark_video_token_v1` (per-token, video-input only),
 * `ark_video_token_tiered_v1` (per-token, video-input × resolution; 2026-08-30),
 * `duration_seconds_v1` (per-second by resolution), `ark_video_seconds_v1`
 * (per-second on the video-input x resolution matrix) and
 * `ark_video_seconds_ref_v1` (per-second by resolution plus a MiniMax A
 * reference-image surcharge). `ark_video_token_v1`
 * and `duration_seconds_v1` previously sat on the `openai` arm — a
 * parking spot chosen only
 * because this enum had no member for the task wire (#1900's note said as
 * much) — and they MOVE rather than widen: `openai` no longer admits either,
 * so a task profile cannot be registered under a chat protocol again. Zero
 * stored rows carried the pair in any environment when this moved (measured,
 * #1949), so no incumbent registration changes meaning.
 *
 * `grok_video_v1` (#1949 B / #2138) admits exactly
 * `GROK_VIDEO_INGRESS_PROFILE_KINDS`: `grok_video_seconds_v1` only.
 * `duration_seconds_v1` stays on `ark_video_v1`. A new tier scheme is a new
 * profile kind (column note on `PROFILE_METERING_CAPABILITY`); Grok's matrix
 * is `{resolution}` with no `has_video_input`, which is this kind, not a
 * reuse of ark's seconds profile. Do NOT add grok to
 * `MODEL_PROTOCOL_VENDOR_ALIASES`: that table is vendor-slug → chat protocol,
 * and Grok video is an explicit registration, not implied by a slug.
 *
 * `grok_image_v1` (#2190) admits exactly `GROK_IMAGE_INGRESS_PROFILE_KINDS`:
 * `grok_image_unit_v1` only. Flat empty `variant_key`, quantity = delivered
 * `data.length`. Do NOT reuse `openai_images_unit_v1` — that form bills
 * ordered `images_requested` and Codex still refuses `n≠1`.
 *
 * WORTH STATING: this table governs the sales-form protocol of a catalog
 * registration, not the upstream wire the gateway dials — an `ark_video_v1`
 * registration is bought over the OpenAI-compatible upstream ladder
 * ({@link SERVING_UPSTREAM_PROTOCOL}), and those are different axes.
 */
const MODEL_PROTOCOL_METERING_PROFILES: Readonly<
  Record<ModelProtocol, readonly MeteringProfileKind[]>
> = {
  anthropic: ['anthropic_messages_v2', 'legacy_4_bucket'],
  openai: [
    'openai_responses_v2',
    'openai_chat_v1',
    'openai_images_v1',
    'text_characters_v1',
    'flat_call_v1',
    'openai_images_unit_v1',
    'legacy_4_bucket',
  ],
  gemini: ['gemini_generate_content_v1', 'legacy_4_bucket'],
  ark_video_v1: [
    'ark_video_token_v1',
    'ark_video_token_tiered_v1',
    'duration_seconds_v1',
    'ark_video_seconds_v1',
    'ark_video_seconds_ref_v1',
  ],
  grok_video_v1: ['grok_video_seconds_v1'],
  grok_image_v1: ['grok_image_unit_v1'],
};

export function isMeteringProfileAllowedForProtocol(
  protocol: ModelProtocol,
  profile: MeteringProfileKind,
): boolean {
  return MODEL_PROTOCOL_METERING_PROFILES[protocol].includes(profile);
}

/**
 * The protocols that admit this metering profile — the INVERSE read of
 * {@link MODEL_PROTOCOL_METERING_PROFILES}, derived rather than declared.
 *
 * A caller that has a profile and needs the `protocols` a legal metering spec
 * must declare has exactly this question, and the answer is already a fact of
 * the pairing table: writing it down a second time would be a table that can
 * disagree with the one the write face enforces. Derivation cannot.
 *
 * Motivating case (#2251): a metering spec whose quantity reads a
 * protocol-scoped descriptor — `duration_seconds` is admitted on
 * `ark_video_v1` and `grok_video_v1`, not everywhere — is refused with
 * `descriptor_protocol_undeclared` unless it names its protocols. The operator
 * console's spec EXAMPLE omitted them, so an operator who pasted the
 * placeholder verbatim got a 400 from the write face; the example is the thing
 * that was wrong, not the validator.
 *
 * Order follows {@link KNOWN_MODEL_PROTOCOLS}, so the result is stable and a
 * generated document does not churn between renders.
 */
export function protocolsAdmittingMeteringProfile(
  profile: MeteringProfileKind,
): readonly ModelProtocol[] {
  return KNOWN_MODEL_PROTOCOLS.filter(
    (protocol) => isMeteringProfileAllowedForProtocol(protocol, profile),
  );
}

export function isModelProtocol(value: unknown): value is ModelProtocol {
  return typeof value === 'string' && (KNOWN_MODEL_PROTOCOLS as readonly string[]).includes(value);
}

/**
 * The upstream wire ladder a registration of each sales-form protocol is
 * SERVED off — the bridge between the two protocol axes (#1949 A).
 *
 * The three chat protocols are served on their own wire, identity-mapped. An
 * `'ark_video_v1'` registration is served off the OpenAI-COMPATIBLE upstream ladder:
 * the Ark task API is resold at OpenAI-compatible bases whose task routes sit
 * beside `/v1/chat/completions` (#1874 §1.1), so upstream capabilities,
 * routing targets and serviceability verdicts for it all live under
 * `'openai'` — which is exactly why the registration itself no longer needs
 * to lie about its own wire. `'grok_video_v1'` identity-maps onto a NEW
 * `UpstreamProtocol` member of the same name: that supply is not an
 * OpenAI-compatible base (owner, 2026-08-26).
 *
 * Consumers: the task ingress's routing resolution (`tasks/route.ts`), the
 * gateway's SKU-activation chain evaluation, and platform's catalog
 * routability intersection — every seat that must translate "which sales
 * form is this row" into "which upstream ladder buys it". Exhaustive by
 * construction: a new `ModelProtocol` member does not compile until its
 * serving ladder is stated here.
 */
export const SERVING_UPSTREAM_PROTOCOL: Readonly<Record<ModelProtocol, UpstreamProtocol>> = {
  anthropic: 'anthropic',
  openai: 'openai',
  gemini: 'gemini',
  ark_video_v1: 'openai',
  grok_video_v1: 'grok_video_v1',
  grok_image_v1: 'grok_image_v1',
};

export type ModelShelfStatus = 'listed' | 'delisted';

export const KNOWN_MODEL_SHELF_STATUSES: readonly ModelShelfStatus[] = [
  'listed',
  'delisted',
] as const;

export function isModelShelfStatus(value: unknown): value is ModelShelfStatus {
  return (
    typeof value === 'string' && (KNOWN_MODEL_SHELF_STATUSES as readonly string[]).includes(value)
  );
}

// ─── models table ─────────────────────────────────────────────────────────

/**
 * Model-intrinsic request-capability bits (router#1322), persisted in
 * `models.request_capabilities_json`. Every key is tri-state: `true` /
 * `false` are explicit operator statements that override the router's
 * built-in per-model defaults; an absent key falls through to those
 * defaults (frozen legacy lists in egress-runtime). Egress-profile
 * `capabilities_json` still outranks this registry when non-null.
 */
export interface ModelRequestCapabilities {
  assistant_prefill?: boolean;
  mid_conversation_system_message?: boolean;
}

const MODEL_REQUEST_CAPABILITY_KEYS = [
  'assistant_prefill',
  'mid_conversation_system_message',
] as const;

/**
 * Strict write-side validator: accepts only a plain object whose keys are
 * known capability names with boolean values. Returns `null` for an empty
 * object (stored as `'{}'`, read back as `null`) and `undefined` for any
 * invalid shape so route handlers can 400 without guessing.
 */
export function normalizeModelRequestCapabilities(
  value: unknown,
): ModelRequestCapabilities | null | undefined {
  if (value === null) return null;
  if (typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const out: { assistant_prefill?: boolean; mid_conversation_system_message?: boolean } = {};
  for (const key of Object.keys(record)) {
    if (!(MODEL_REQUEST_CAPABILITY_KEYS as readonly string[]).includes(key)) {
      return undefined;
    }
    const bit = record[key];
    if (typeof bit !== 'boolean') return undefined;
    out[key as (typeof MODEL_REQUEST_CAPABILITY_KEYS)[number]] = bit;
  }
  return Object.keys(out).length === 0 ? null : out;
}

/**
 * Lenient read-side parser for the persisted JSON column: unknown keys and
 * non-boolean values are dropped instead of failing the row. Returns `null`
 * when nothing usable remains.
 */
export function parseModelRequestCapabilitiesJson(
  json: string | null | undefined,
): ModelRequestCapabilities | null {
  if (json === null || json === undefined || json.trim().length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const record = parsed as Record<string, unknown>;
  const out: { assistant_prefill?: boolean; mid_conversation_system_message?: boolean } = {};
  for (const key of MODEL_REQUEST_CAPABILITY_KEYS) {
    const bit = record[key];
    if (typeof bit === 'boolean') {
      out[key] = bit;
    }
  }
  return Object.keys(out).length === 0 ? null : out;
}

export interface ModelAliasRecord {
  vendor: string;
  /** Canonical model id the alias resolves to. */
  model: string;
  /** Alternate model id accepted from clients for this vendor. */
  alias: string;
  created_at: string;
}

/**
 * Shape returned for every model row. Mirrors `models` plus a convenience
 * `deleted_at` (null for live entries). Callers filter deleted rows with
 * `?include_deleted=true` opt-in.
 */
export interface ModelRecord {
  vendor: string;
  model: string;
  /** Marketing name, e.g. "Claude Opus 4.7". NULL in DB surfaces as `null`. */
  display_name: string | null;
  /** Free-form family tag, e.g. "claude-4". NULL if not set. */
  family: string | null;
  context_window: number | null;
  /** Hard cap on a single response. NULL when upstream enforces. */
  max_output_tokens: number | null;
  supports_streaming: boolean;
  supports_tool_use: boolean;
  supports_vision: boolean;
  supports_prompt_cache: boolean;
  shelf_status: ModelShelfStatus;
  shelf_updated_at: string | null;
  shelf_reason: string | null;
  created_at: string;
  /** ISO-8601; non-null means soft-deleted and hidden from default list. */
  deleted_at: string | null;
  /**
   * ISO-8601 stamp of when ops marked the model deprecated. Distinct from
   * `deleted_at`: deprecated models are still listable, chargeable, and
   * routable — ops just signals that new adoption should be discouraged.
   * Toggled via POST `/internal/models/:vendor/:model/deprecate` +
   * POST `/internal/models/:vendor/:model/undeprecate`. PATCH does NOT
   * accept this column.
   */
  deprecated_at: string | null;
  /**
   * Short operator-facing explanation shown alongside the deprecation
   * chip. NULL when the model is live. Conventionally <= 512 chars but
   * not enforced in DB so ops can paste fuller migration instructions.
   */
  deprecation_notice: string | null;
  /**
   * Model-intrinsic request-capability overrides (router#1322). `null` (or
   * absent, from a platform that predates the field) when the registry has
   * no explicit bits and built-in defaults decide.
   */
  request_capabilities?: ModelRequestCapabilities | null;
  /**
   * Alternate model ids accepted from tenant requests. Detail and list
   * endpoints include this when available; older callers may omit it.
   */
  aliases?: ModelAliasRecord[];
  /**
   * Current effective pricing snapshot when the record is returned from the
   * list endpoint. Detail endpoints may omit it and callers should treat
   * missing the same as `null`.
   */
  current_pricing?: ModelPricingRecord | null;
}

export interface CreateModelRequest {
  vendor: string;
  model: string;
  display_name?: string | null;
  family?: string | null;
  context_window?: number | null;
  max_output_tokens?: number | null;
  supports_streaming?: boolean;
  supports_tool_use?: boolean;
  supports_vision?: boolean;
  supports_prompt_cache?: boolean;
  shelf_status?: ModelShelfStatus;
  shelf_reason?: string | null;
  request_capabilities?: ModelRequestCapabilities | null;
}

export interface CreateModelResponse {
  model: ModelRecord;
}

export interface ListModelsResponse {
  models: ModelRecord[];
}

export interface GetModelResponse {
  model: ModelRecord;
}

export interface CreateModelAliasRequest {
  alias: string;
}

export interface CreateModelAliasResponse {
  alias: ModelAliasRecord;
}

export interface DeleteModelAliasResponse {
  deleted: true;
  alias: ModelAliasRecord;
}

/**
 * Partial update. Undefined fields are left unchanged. Setting `display_name`
 * or `family` to `null` clears the column. Primary key columns (`vendor`,
 * `model`) are path parameters and cannot be renamed via PATCH.
 */
export interface UpdateModelRequest {
  display_name?: string | null;
  family?: string | null;
  context_window?: number | null;
  max_output_tokens?: number | null;
  supports_streaming?: boolean;
  supports_tool_use?: boolean;
  supports_vision?: boolean;
  supports_prompt_cache?: boolean;
  /** `null` clears every explicit bit (registry falls back to defaults). */
  request_capabilities?: ModelRequestCapabilities | null;
}

export interface UpdateModelResponse {
  model: ModelRecord;
}

export interface DeleteModelResponse {
  model: ModelRecord;
}

/**
 * POST /internal/models/:vendor/:model/deprecate — stamp `deprecated_at`
 * (CURRENT_TIMESTAMP) + `deprecation_notice`. 409 MODEL_ALREADY_DEPRECATED
 * when already stamped (callers update notice via a second
 * undeprecate → deprecate cycle to keep audit rows clean).
 */
export interface DeprecateModelRequest {
  /** Short operator-facing explanation. Must be non-empty. */
  deprecation_notice: string;
}

export interface DeprecateModelResponse {
  model: ModelRecord;
}

/**
 * POST /internal/models/:vendor/:model/undeprecate — clear both
 * `deprecated_at` and `deprecation_notice`. 409 MODEL_NOT_DEPRECATED
 * when the row is already live.
 */
export interface UndeprecateModelResponse {
  model: ModelRecord;
}

export interface UpdateModelShelfRequest {
  reason?: string | null;
  /** Canonical platform SKU whose activation this shelf mutation serves. */
  sku_id?: string | null;
}

export interface UpdateModelShelfResponse {
  model: ModelRecord;
  /** Absent for a no-op; present only for an atomically committed owner mutation. */
  activation_receipt?: {
    receipt_id: string;
    plan_id: string;
    mutation_id: string;
    sku_id: string | null;
    owner_revision: string;
    rollback: {
      strategy: 'cas';
      operation_id: string;
      expected_revision: string;
      endpoint: string;
    };
  };
}

// ─── model_pricing table (append-only by effective_from) ─────────────────

/**
 * One pricing generation. Historical cost reconstruction picks the row with
 * the greatest `effective_from <= usage_log.created_at`. Earlier rows are
 * retained; callers never mutate them.
 */
export interface ModelPricingRecord {
  vendor: string;
  model: string;
  /** ISO-8601 UTC. When this pricing takes effect. */
  effective_from: string;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd: number;
  cache_write_per_mtok_usd: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
  metering_profile?: MeteringProfileKind | null;
  created_at: string;
  /** Prompt-length tiers of this generation, min_prompt_tokens ASC. Omitted when none. */
  tiers?: PricingTierRecord[];
  /**
   * TOKEN-lane price tiers of this generation (#1937), `variant_key` ASC.
   * Omitted when none, exactly like `tiers` — so a generation that has never
   * had a variant row is byte-identical to what this face returned before
   * this key existed, and the tenant-facing readers that share this type see
   * no new field on any row they render today.
   *
   * ADDITIVE ONLY. This is the ops read face's half of the write face's
   * `variants` input, and it exists because the console could not previously
   * SEE this dimension: `listModelPricing` selected `model_pricing_tiers` and
   * nothing else, so an operator editing a price list had no way to know a
   * tier axis was in force on it.
   */
  variants?: PricingVariantRecord[];
}

/**
 * One prompt-length tier row of a pricing generation. The base
 * `model_pricing` row is the implicit `min_prompt_tokens = 0` tier.
 */
export interface PricingTierRecord {
  vendor: string;
  model: string;
  effective_from: string;
  min_prompt_tokens: number;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd: number;
  cache_write_per_mtok_usd: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
  created_at: string;
}

/**
 * One TOKEN-lane price tier of a pricing generation (#1937). The base
 * `model_pricing` row is the implicit `variant_key: ''` tier.
 *
 * Column-for-column {@link PricingTierRecord} with `variant_key` where
 * `min_prompt_tokens` was, because the two are the same kind of object — a
 * different price list for a differently-shaped request — and migration 0038
 * gave them the same table shape for that reason.
 */
export interface PricingVariantRecord {
  vendor: string;
  model: string;
  effective_from: string;
  variant_key: string;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd: number;
  cache_write_per_mtok_usd: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
  created_at: string;
}

export interface CreatePricingTierInput {
  min_prompt_tokens: number;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd?: number;
  cache_write_per_mtok_usd?: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
}

export interface CreatePricingRequest {
  effective_from: string;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd?: number;
  cache_write_per_mtok_usd?: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
  /** Written transactionally with the base row; base owns min_prompt_tokens 0. */
  tiers?: CreatePricingTierInput[];
  /**
   * TOKEN-lane price tiers of this generation (#1937), written in the same
   * transaction as the base row: a generation is either fully priced or
   * absent.
   *
   * A variant row REPLACES the base rates for a request whose resolved
   * `variant_key` matches it, so every rate column it needs must be on the
   * row — there is no partial override, because a half-overridden price list
   * would leave the unlisted dimensions priced at a rate sized for a
   * differently-shaped request.
   */
  variants?: CreatePricingVariantInput[];
}

/**
 * One TOKEN-lane price tier being written (#1937). `variant_key` obeys
 * {@link UNIT_VARIANT_KEY_RE}, the same rule the unit lane's rows obey and
 * the same one the metering-spec validator now DERIVES its template check
 * from — one rule, three faces, no parallel regex to drift.
 */
export interface CreatePricingVariantInput {
  variant_key: string;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd?: number;
  cache_write_per_mtok_usd?: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
}

export interface CreatePricingResponse {
  pricing: ModelPricingRecord;
}

export interface ListPricingResponse {
  /** Newest-first by `effective_from`. */
  pricing: ModelPricingRecord[];
}

export interface CurrentPricingResponse {
  pricing: ModelPricingRecord;
}

// ─── model_unit_pricing table (append-only by effective_from) ─────────────

/**
 * Ceiling on rate rows per unit pricing generation. Same shape as
 * MAX_PRICING_TIERS: 4 units x a handful of variants. Shared rather than
 * owned by the platform write face so the ops-console form refuses the 17th
 * row with the same number the platform would 400 with (#1669).
 */
export const MAX_UNIT_PRICING_RATES = 16;

/**
 * Non-empty unit-pricing variant keys take the channel-slug charset (SLUG_RE
 * in platform routes/internal-channels.ts, the nearest slug convention in
 * the repo), widened only to admit a leading digit — real variants are
 * resolution/tier names like `1080p`. `''` is the default row and is legal
 * by omission. Shared with the console form for the same reason as
 * MAX_UNIT_PRICING_RATES.
 */
export const UNIT_VARIANT_KEY_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

/**
 * One unit price row of a unit pricing generation (#1651). A generation is
 * the set of rows sharing (vendor, model, effective_from); rating resolves
 * the generation with the greatest `effective_from <= event_at` and matches
 * (unit, variant_key) inside it, `''` being the default row a missing
 * variant falls back to. `rate_usd_micros_per_unit` is an INTEGER micro-USD
 * amount per unit — see `UnitPricingRate` in metering.ts, which this row
 * feeds verbatim.
 */
export interface ModelUnitPricingRecord {
  vendor: string;
  model: string;
  /** ISO-8601 UTC. When this unit pricing generation takes effect. */
  effective_from: string;
  unit: UnitUsageUnit;
  /** `''` means the default row for the unit. */
  variant_key: string;
  /** Positive safe integer, micro-USD per unit. */
  rate_usd_micros_per_unit: number;
  created_at: string;
}

export interface CreateUnitPricingRateInput {
  unit: UnitUsageUnit;
  /** Omitted or `''` writes the default row for the unit. */
  variant_key?: string;
  rate_usd_micros_per_unit: number;
}

/** One whole generation, written transactionally: all rows or none. */
export interface CreateUnitPricingRequest {
  effective_from: string;
  rates: CreateUnitPricingRateInput[];
}

export interface CreateUnitPricingResponse {
  unit_pricing: ModelUnitPricingRecord[];
}

export interface ListUnitPricingResponse {
  /** Newest-first by `effective_from`, then unit, then variant_key. */
  unit_pricing: ModelUnitPricingRecord[];
}

// ─── model_protocols table ────────────────────────────────────────────────

export interface ModelProtocolRecord {
  vendor: string;
  model: string;
  protocol: ModelProtocol;
  /**
   * The metering profile actually in force for this (vendor, model, protocol).
   *
   * Reported rather than left to be inferred from the protocol: since #1557
   * the profile is what authorizes the image lane, so two models on the same
   * `openai` protocol can meter — and bill — completely differently, and an
   * operator who cannot see which is which cannot tell an image SKU from a
   * text one. `null` means the row is missing, which is a defect rather than a
   * default: the protocol registration writes it.
   */
  metering_profile: MeteringProfileKind | null;
  /**
   * The declarative metering spec stored on this registration (#1878), or
   * `null` when the row carries none and the built-in for its profile governs.
   *
   * Optional on the TYPE only so that a consumer generation older than #1878
   * still typechecks; the platform always reports it.
   *
   * `null` is reported for an illegal stored document as well as for an absent
   * one. The write face refuses illegal documents 400, so the two can only be
   * told apart when someone wrote past it with direct SQL — at which point the
   * SKU is refused at the route gate and the registration is what to re-issue
   * either way.
   */
  metering_spec?: MeteringSpec | null;
  /**
   * The per-tier-class in-flight allowance stored on this registration
   * (#1900, standalone since #1883 — the parameter surface it used to pair
   * with is retired; the ark parameter vocabulary is protocol code), or
   * `null` when the row carries none.
   *
   * There is NO built-in to fall back to, so `null` on a task-ingress
   * registration means the SKU is refused at the route gate rather than
   * served ungated. The write face refuses that combination 400, so a `null`
   * here on a task SKU can only come from direct SQL.
   */
  task_admission?: ArkTaskAdmissionSurface | null;
}

export interface CreateProtocolRequest {
  protocol: ModelProtocol;
  /** Optional override; omitted means the platform default for the protocol. */
  metering_profile?: MeteringProfileKind;
  /**
   * The declarative metering spec for this SKU (#1878, ADR-012 §3). Omitted or
   * `null` stores no spec, which means the built-in for the profile governs.
   *
   * EITHER LANE, and its `lane` must be the lane `metering_profile` bills on
   * (`meteringLaneForProfileKind`) — a unit-lane document on a token profile
   * and a token-lane one on a unit profile are both 400, because in each case
   * the reader that governs the profile answers `null` for it. The token lane
   * became writable with #1937's price-tier axis; before that this face
   * refused every `lane: 'token'` document, which is why a tiered SKU could
   * not be configured through it at all.
   *
   * OMITTING IT ON A RE-REGISTRATION CLEARS ANY STORED SPEC, the same rule
   * `metering_profile` already obeys: re-registering lands exactly what a
   * first registration would have, so the request is the whole truth about the
   * row rather than a patch against whatever was there.
   */
  metering_spec?: MeteringSpec | null;
  /**
   * The per-tier-class in-flight allowance for this SKU (#1900, review item
   * R4; standalone since #1883). Required when `metering_profile` is a
   * task-ingress profile and refused otherwise.
   *
   * Its `max_in_flight_by_tier_class` KEY SET is the whole enumeration of
   * servable tier classes — the parameter surface it used to be validated
   * against is retired (the ark vocabulary is protocol code and value domains
   * are the upstream's), so listing a tier IS writing its key here, and a
   * tier with no key is refused at the route gate before any spend. R4's
   * force is unchanged: nobody can list a tier without stating its exposure.
   *
   * `task_parameter_surface` is RETIRED: a request that still carries the
   * field is refused 400 by name, so a stale client learns the law changed
   * rather than having its document silently dropped.
   */
  task_admission?: ArkTaskAdmissionSurface | null;
}

export interface CreateProtocolResponse {
  protocol: ModelProtocolRecord;
}

export interface ListProtocolsResponse {
  protocols: ModelProtocolRecord[];
}

export interface DeleteProtocolResponse {
  protocol: ModelProtocolRecord;
}
